import { useCustom, useCustomMutation } from '@refinedev/core';
import { Table, Tag, Button, message } from 'antd';
import { Cpu, Play } from 'lucide-react';
import { useState, type FC } from 'react';

import AiProviderEditModal from '@/components/ops/AiProviderEditModal';

interface AiProviderConfig {
  taskType: string;
  provider: string;
  model: string;
  fallbackProvider: string | null;
  fallbackModel: string | null;
  active: boolean;
}

const TASK_TYPE_LABELS: Record<string, string> = {
  feedback_analysis: 'Analyse feedback',
  test_recipe_generation: 'Generation recettes',
  sav_diagnosis: 'Diagnostic SAV',
  code_review: 'Code review',
  summary: 'Resume',
  translation: 'Traduction',
  embedding: 'Embedding',
  chat: 'Chat',
  realtime: 'Temps reel',
  tts: 'Text-to-Speech',
  stt: 'Speech-to-Text',
};

const PROVIDER_COLORS: Record<string, string> = {
  openai: 'blue',
  anthropic: 'purple',
  mistral: 'orange',
  ollama: 'green',
  groq: 'cyan',
};

const AiConfigPage: FC = () => {
  const [editRecord, setEditRecord] = useState<AiProviderConfig | null>(null);
  const [testingTask, setTestingTask] = useState<string | null>(null);

  const { data, isLoading, refetch } = useCustom<{ data: { items: AiProviderConfig[] } }>({
    url: '/api/v1/admin/ai/providers',
    method: 'get',
  });

  const { mutate: testProvider } = useCustomMutation();

  const providers = data?.data?.data?.items ?? [];

  const handleTest = (taskType: string) => {
    setTestingTask(taskType);
    testProvider(
      {
        url: '/api/v1/admin/ai/providers/test',
        method: 'post',
        values: { taskType },
      },
      {
        onSuccess: () => {
          void message.success(`Test "${TASK_TYPE_LABELS[taskType] ?? taskType}" reussi`);
          setTestingTask(null);
        },
        onError: () => {
          void message.error(`Test "${TASK_TYPE_LABELS[taskType] ?? taskType}" echoue`);
          setTestingTask(null);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <GradientHeader />

      <div className="fc-card overflow-hidden p-0">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Configuration des providers IA
          </h3>
        </div>
        <Table
          dataSource={providers}
          loading={isLoading}
          rowKey="taskType"
          size="middle"
          scroll={{ x: 900 }}
          pagination={false}
        >
          <Table.Column
            dataIndex="taskType"
            title="Type de tache"
            render={(t: string) => (
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {TASK_TYPE_LABELS[t] ?? t}
              </span>
            )}
          />
          <Table.Column
            dataIndex="provider"
            title="Provider"
            render={(p: string) => (
              <Tag color={PROVIDER_COLORS[p] ?? 'default'}>{p}</Tag>
            )}
          />
          <Table.Column
            dataIndex="model"
            title="Modele"
            render={(m: string) => (
              <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{m}</span>
            )}
          />
          <Table.Column
            dataIndex="fallbackProvider"
            title="Fallback Provider"
            render={(p: string | null) =>
              p ? <Tag color={PROVIDER_COLORS[p] ?? 'default'}>{p}</Tag> : <span className="text-slate-400">--</span>
            }
          />
          <Table.Column
            dataIndex="fallbackModel"
            title="Fallback Modele"
            render={(m: string | null) =>
              m ? (
                <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{m}</span>
              ) : (
                <span className="text-slate-400">--</span>
              )
            }
          />
          <Table.Column
            dataIndex="active"
            title="Actif"
            width={80}
            render={(active: boolean) => (
              <Tag color={active ? 'success' : 'default'}>
                {active ? 'Oui' : 'Non'}
              </Tag>
            )}
          />
          <Table.Column
            title="Actions"
            width={150}
            render={(_: unknown, r: AiProviderConfig) => (
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  onClick={() => setEditRecord(r)}
                >
                  Modifier
                </Button>
                <Button
                  size="small"
                  type="text"
                  icon={<Play className="h-3.5 w-3.5" />}
                  loading={testingTask === r.taskType}
                  onClick={() => handleTest(r.taskType)}
                  title="Tester"
                />
              </div>
            )}
          />
        </Table>
      </div>

      <AiProviderEditModal
        record={editRecord}
        onClose={() => setEditRecord(null)}
        onSuccess={() => {
          setEditRecord(null);
          void refetch();
        }}
      />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <Cpu className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Configuration IA</h1>
        <p className="mt-1 text-sm text-blue-100">
          Routage des providers et modeles par type de tache
        </p>
      </div>
    </div>
  </div>
);

export default AiConfigPage;
